import { AxiosError } from 'axios'
import { merge } from 'lodash'
import { toast, ToastOptions } from 'react-toastify'

/**
 * Mapping of field name used in the back-end to the label of that same field on
 * the product form.  Only fields that don't have an intuitive match should be
 * added. Ex. in the back-end 'kind' is the name of the field that is labeled
 * 'Product Type' on the product form.
 */
const FIELD_NAME_USER_ALIASES: { [key: string]: string } = {
  kind: 'Product Type',
  category: 'Lineage',
}

export const handleValidationErrors = (validations: { [key: string]: string[] }) => {
  // If there is a conflict on name field notify user
  if (validations.hasOwnProperty('name')) {
    const idRegEx = /is already taken by product (?<id>\d+)/
    const message = validations.name.find((message: string) => message.match(idRegEx))
    const matchResult = message?.match(idRegEx)
    const conflictingId = matchResult && matchResult.groups?.id

    if (conflictingId) {
      toast(`Error: name ${message}`, {
        containerId: 'error',
        onClick: () => {
          navigator.clipboard.writeText(conflictingId).then(() => {
            toast(`Product ID ${conflictingId} copied to clipboard`, {
              containerId: 'default',
            })
          })
        },
      })
      return
    }
  }

  // Otherwise notify user of first validation error
  const firstKey = Object.keys(validations)[0]
  const message = validations[firstKey][0]
  toast(`Error: ${FIELD_NAME_USER_ALIASES[firstKey] || firstKey} ${message}`, {
    containerId: 'error',
  })
}

/**
 * This function will display an error toast from the given AxiosError response.
 * If a message is passed this will be appended to the generic error text in the
 * toast. It uses react-toastify behind the scenes to display errors, if an options
 * object is passed it will be merged with the toastify options.
 */
export function handleGenericErrors(error: AxiosError, options?: ToastOptions): void
export function handleGenericErrors(
  error: AxiosError,
  message?: string,
  options?: ToastOptions
): void
export function handleGenericErrors(
  error: AxiosError,
  message?: string | ToastOptions,
  options = {}
): void {
  // handle overload parameter parsing
  if (typeof message === 'object' && message !== null) {
    options = message
    message = undefined
  }
  let errorMessage = error.message
  const responseError = error?.response?.data?.errors?.error
  const validations = error?.response?.data?.errors?.validations

  if (message) {
    errorMessage += `, ${message} `
  } else if (responseError) {
    errorMessage += `: ${responseError}`
  }

  if (validations && validations.length > 0) {
    errorMessage += `- Violations: ${validations.join(', ')}`
  }

  const defaultOptions = {
    containerId: 'error',
    onClick: () => {
      const method = error.response?.config.method?.toUpperCase()
      const url = error.response?.config.url
      navigator.clipboard.writeText(`${method} to ${url} ${errorMessage}`).then(() => {
        toast('Error copied to the clipboard.', {
          containerId: 'default',
        })
      })
    },
  }

  const toastOptions = merge(defaultOptions, options)

  toast(errorMessage, toastOptions)
}

export const handleNotFoundErrors = (entity: string, error: AxiosError, message?: string) => {
  let errorMessage = `${error.message}, ${entity} not found`
  if (message) {
    errorMessage += `, ${message} `
  }

  toast(errorMessage, {
    containerId: 'error',
    onClick: () => {
      const method = error.response?.config.method?.toUpperCase()
      const url = error.response?.config.url
      navigator.clipboard.writeText(`${method} to ${url} ${errorMessage}`).then(() => {
        toast('Error copied to the clipboard.', {
          containerId: 'default',
        })
      })
    },
  })
}
